// src/components/common/Avatar/ProfileAvatar.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  Users,
  BookOpen,
  CreditCard,
  User,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  UserPlus,
  GraduationCap,
  Eye,
  ShieldCheck
} from "lucide-react";
import logo from '../../../Images/logo.png';
import walkinStatusImage from '../../../Images/database.png';
import rupees from '../../../Images/rupee-indian.png';
import "../../../styles/ProfileAvatar.css"
import MyProfile from "../../../pages/MyProfile";
import EmployeeForm from "../../employees/EmployeeForm";
import CourseCreationForm from "../../courses/CourseCreationForm";
import CourseDetailsView from "../../courses/CourseDetailsView";
import { fetchUserData } from "../../../services/authServicepa";
import { checkPermissions } from "../../../utils/permissions";

const ProfileAvatar = () => {
  const [email, setEmail] = useState("");
  const [userRole, setUserRole] = useState("");
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [showCourseDetails, setShowCourseDetails] = useState(false);
  const [showMyProfile, setShowMyProfile] = useState(false); // Add this state
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const navigate = useNavigate();

  // Fetch user email, role and available branches/locations from API
  useEffect(() => {
    const getUserData = async () => {
      try {
        const userData = await fetchUserData();
        if (userData) {
          setEmail(userData.email_id || "");
          setUserRole(userData.role || "");
          setBranches(userData.branch || []);
          setLocations(userData.location || []);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    getUserData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("emp_name");
    localStorage.removeItem("placementFilterState");
    localStorage.removeItem("placementDbFilters");
    localStorage.removeItem("placedStudentFilters");
    localStorage.removeItem("jobDashboardFilters");
    localStorage.removeItem("jobDashboard-selectedColumns");
    sessionStorage.removeItem("lastActiveTab");
    sessionStorage.removeItem("lastJobReqPath");

    setEmail("");
    navigate("/", { replace: true });
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Add this function to handle My Profile click
  const handleMyProfileClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMyProfile(true);
  };

  const handleMouseEnter = (e, label) => {
    if (isCollapsed) {
      setTooltip({
        show: true,
        text: label,
        x: e.clientX + 15,
        y: e.clientY - 10
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isCollapsed && tooltip.show) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX + 15,
        y: e.clientY - 10
      }));
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed) {
      setTooltip({ show: false, text: '', x: 0, y: 0 });
    }
  };

  const navItems = [
    // Main Navigation
    ...(["Super-Admin", "Branch-head"].includes(userRole) ? [{
      label: "Home Page",
      icon: Home,
      action: () => navigate("/landingpage")
    }] : []),

    ...(["BDE", "Branch-head", "Super-Admin"].includes(userRole) ? [{
      label: "Walk-In Status",
      icon: "image", // Special flag to indicate this should use an image
      imageSrc: walkinStatusImage, // Add image source
      action: () => navigate("/walkin-dashboard")
    }] : []),

    // Course Management
    ...(checkPermissions.canCreateCourse(userRole) ? [{
      label: "Create Course Details",
      icon: BookOpen,
      action: () => setShowCourseForm(true)
    }] : []),

    ...(checkPermissions.canViewCourseDetails(userRole) ? [{
      label: "View Course Details",
      icon: Eye,
      action: () => setShowCourseDetails(true)
    }] : []),
    ...(["BDE", "Branch-head", "Super-Admin"].includes(userRole) ? [{
      label: "Course Completed Dashboard",
      icon: GraduationCap, // or another suitable icon
      action: () => navigate("/course-completed")
    }] : []),


    // Payment Management
    ...(["Branch-head", "Super-Admin"].includes(userRole) ? [{
      label: "Payment Records",
      icon: CreditCard,
      action: () => navigate("/payment-records")
    }] : []),

    ...(["Branch-head", "Super-Admin", "BDE"].includes(userRole) ? [{
      label: "Pending Fees Record",
      icon: "image",
      imageSrc: rupees, // Add image source
      action: () => navigate("/pending-records")
    }] : []),

    // Employee Management
    ...(checkPermissions.canCreateEmployee(userRole) ? [{
      label: "Create Employee",
      icon: UserPlus,
      action: () => setShowEmployeeForm(true)
    }] : []),

    ...(checkPermissions.canViewEmployees(userRole) ? [{
      label: "View Employees",
      icon: Users,
      action: () => navigate("/employees")
    }] : []),
  ];

  const settingsItems = [
    ...(["Super-Admin"].includes(userRole) ? [{
      label: "Reset to Default Password",
      icon: Settings,
      action: () => navigate("/defaultpassword")
    }] : []),
    ...(["Super-Admin"].includes(userRole) ? [{
      label: "Reset to Default Password for students",
      icon: ShieldCheck,
      action: () => navigate("/student-resetdefaultpassword")
    }] : []),
    {
      label: "Logout",
      icon: LogOut,
      action: handleLogout
    }
  ];

  return (
    <>
      <div className={`avatar-container ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="avatar-profile-container">
          <div className="avatar-button">
            <img src={logo} alt="PUMO TECHNOVATION" className="logo-image" />
          </div>

          <div className="avatar-emp-name">PUMO TECHNOVATION</div>
          <button
            className="avatar-dropdown-trigger"
            onClick={toggleSidebar}
          >
            {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        <div className="avatar-dropdown">
          <div className="dropdown-email">{email || "Fetching..."}</div>

          {/* Main Navigation Section */}
          <div className="nav-section">
            <div className="nav-section-title">Main</div>
            {navItems.map((item, index) => {
              return (
                <button
                  key={index}
                  className="dropdown-item"
                  onClick={item.action}
                  onMouseEnter={(e) => handleMouseEnter(e, item.label)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  {/* Conditionally render image or icon */}
                  {item.icon === "image" ? (
                    <img
                      src={item.imageSrc}
                      alt={item.label}
                      className="navsb-image"
                      style={{
                        width: '20px',
                        height: '20px',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    React.createElement(item.icon, { size: 20, className: "navsb-icon" })
                  )}
                  <span className="nav-label">{item.label}</span>
                </button>
              );
            })}
          </div>

          {/* Profile Section */}
          <div className="nav-section">
            <div className="nav-section-title">Profile</div>
            <button
              className="dropdown-item"
              onClick={handleMyProfileClick} // Add onClick handler
              onMouseEnter={(e) => handleMouseEnter(e, "My Profile")}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <User size={20} className="navsb-icon" />
              <span className="nav-label">My Profile</span> {/* Just text, not component */}
            </button>
          </div>

          {/* Settings Section */}
          <div className="nav-section">
            <div className="nav-section-title">Settings</div>
            {settingsItems.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={index}
                  className="dropdown-item"
                  onClick={item.action}
                  onMouseEnter={(e) => handleMouseEnter(e, item.label)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  <IconComponent size={20} className="navsb-icon" />
                  <span className="nav-label">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cursor Following Tooltip */}
      {tooltip.show && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            background: '#1e293b',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            zIndex: 9999,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            pointerEvents: 'none',
            transform: 'translateY(-50%)'
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Modal Forms */}
      {showEmployeeForm && (
        <EmployeeForm
          branches={branches}
          locations={locations}
          userRole={userRole}
          onClose={() => setShowEmployeeForm(false)}
        />
      )}

      {showCourseForm && (
        <CourseCreationForm
          onClose={() => setShowCourseForm(false)}
        />
      )}

      {showCourseDetails && (
        <CourseDetailsView
          onClose={() => setShowCourseDetails(false)}
          onDelete={(courseId) => console.log('Course deleted:', courseId)}
        />
      )}

      {/* My Profile Modal */}
      {showMyProfile && (
        <MyProfile onClose={() => setShowMyProfile(false)} />
      )}
    </>
  );
};

export default ProfileAvatar;